import type { ReactNode } from "react";
import { SegmentedControl } from "./SegmentedControl";

// Three-way filter (Egal / Ja / Nein, All / Yes / No). The `boolean | null`
// public value is the natural domain shape for a tri-state filter; the
// encode/decode to SegmentedControl's string vocabulary lives once, here, so
// the encoding choice (null === "all", true === "yes", false === "no") never
// has to be rediscovered at call sites. Visual fidelity is owned by
// SegmentedControl, which this component delegates to without restyling.

export type TriStateValue = boolean | null;

type Internal = "all" | "yes" | "no";

interface TriStateControlProps {
  value: TriStateValue;
  onChange: (value: TriStateValue) => void;
  labels: { all: ReactNode; yes: ReactNode; no: ReactNode };
  size?: "sm" | "md";
  fullWidth?: boolean;
  className?: string;
  "aria-label"?: string;
}

function encode(v: TriStateValue): Internal {
  if (v === null) return "all";
  return v ? "yes" : "no";
}

function decode(s: Internal): TriStateValue {
  if (s === "all") return null;
  return s === "yes";
}

export function TriStateControl({
  value,
  onChange,
  labels,
  size,
  fullWidth,
  className,
  ...rest
}: TriStateControlProps) {
  return (
    <SegmentedControl
      {...rest}
      size={size}
      fullWidth={fullWidth}
      className={className}
      value={encode(value)}
      onChange={(s) => onChange(decode(s))}
      options={[
        { value: "all", label: labels.all },
        { value: "yes", label: labels.yes },
        { value: "no", label: labels.no },
      ]}
    />
  );
}
